import { useState } from "react";
import { useTranslation } from "react-i18next";
import Navigation from "@/components/Navigation";
import CookieNotice from "@/components/CookieNotice";
import BookChapter from "@/components/BookChapter";
import BookTableOfContents from "@/components/BookTableOfContents";
import BookReadingControls from "@/components/BookReadingControls";
import { chapters, bookMetadata } from "@/data/bookOfComfort";
import { cn } from "@/lib/utils";
import { BookOpen, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";

const BookOfComfort = () => {
  const { t } = useTranslation();
  const [fontSize, setFontSize] = useState(16);
  const [darkMode, setDarkMode] = useState(false);

  return (
    <Navigation>
      <CookieNotice />

      <div className={cn(
        "min-h-screen transition-colors duration-300",
        darkMode ? "bg-gray-900 text-gray-100" : "bg-gray-50 text-gray-900"
      )}>
        {/* Hero Section with Book Cover */}
        <section className={cn(
          "px-4 py-10 md:px-6 md:py-8",
          darkMode ? "bg-gray-800" : "bg-gradient-to-br from-[#006B5F]/5 via-[#006B5F]/8 to-[#006B5F]/10"
        )}>
          <div className="container mx-auto max-w-5xl">
            <div className="grid md:grid-cols-2 gap-10 md:gap-8 items-center">
              {/* Book Cover Image */}
              <div className="flex justify-center md:justify-end">
                <div className={cn(
                  "relative rounded-xl overflow-hidden shadow-xl transform hover:scale-105 transition-transform duration-300",
                  "max-w-[280px] md:max-w-xs w-full"
                )}>
                  <img
                    src="/images/BookOfComfort.png"
                    alt="Book of Comfort Cover"
                    className="w-full h-auto"
                  />
                  {/* Subtle overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#006B5F]/10 to-transparent pointer-events-none" />
                </div>
              </div>

              {/* Book Info */}
              <div className="text-center md:text-left space-y-4 md:space-y-3">
                <h1 className={cn(
                  "text-3xl md:text-4xl lg:text-5xl font-bold",
                  darkMode ? "text-white" : "text-gray-900"
                )}>
                  {bookMetadata.title}
                </h1>
                <p className={cn(
                  "text-lg md:text-base",
                  darkMode ? "text-gray-300" : "text-gray-600"
                )}>
                  by {bookMetadata.author}
                </p>
                <p className={cn(
                  "text-base md:text-sm italic",
                  darkMode ? "text-gray-400" : "text-gray-500"
                )}>
                  Free e-book for those suffering
                </p>
                <div className="flex flex-col gap-3 items-center md:items-start w-full sm:w-auto pt-2">
                  <Button
                    onClick={() => {
                      const chaptersSection = document.getElementById('chapters-section');
                      chaptersSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className={cn(
                      "bg-[#006B5F] hover:bg-[#005548] text-white shadow-lg w-full sm:w-auto",
                      darkMode && "bg-[#006B5F] hover:bg-[#005548]"
                    )}
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    <span>10 Chapters • 20 min read</span>
                  </Button>
                  <Button
                    asChild
                    className={cn(
                      "bg-[#006B5F] hover:bg-[#005548] text-white shadow-lg w-full sm:w-auto",
                      darkMode && "bg-[#006B5F] hover:bg-[#005548]"
                    )}
                  >
                    <a
                      href="https://www.youtube.com/playlist?list=PLwvMe3dvq_AY5lSkhW0gmB_LoGOc2BdcZ"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <Headphones className="w-4 h-4" />
                      <span>Listen on YouTube</span>
                    </a>
                  </Button>
                  <Button
                    asChild
                    className={cn(
                      "bg-[#006B5F] hover:bg-[#005548] text-white shadow-lg w-full sm:w-auto",
                      darkMode && "bg-[#006B5F] hover:bg-[#005548]"
                    )}
                  >
                    <a
                      href="https://soundcloud.com/user-453190431/sets/book-of-comfort"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <Headphones className="w-4 h-4" />
                      <span>Listen on SoundCloud</span>
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className={cn(
          "border-b px-4 py-10 md:px-6 md:py-10",
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        )}>
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-3 gap-6 md:gap-5">
              {bookMetadata.testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={cn(
                    "rounded-lg p-5 sm:p-6",
                    darkMode ? "bg-gray-700" : "bg-gray-50"
                  )}
                >
                  <p className={cn(
                    "italic mb-3 text-sm md:text-xs",
                    darkMode ? "text-gray-300" : "text-gray-700"
                  )}>
                    "{testimonial.quote}"
                  </p>
                  <p className={cn(
                    "text-xs font-semibold",
                    darkMode ? "text-gray-400" : "text-gray-600"
                  )}>
                    — {testimonial.author}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Dedication */}
        <section className={cn(
          "px-4 py-10 md:px-6 md:py-12",
          darkMode ? "bg-gray-900" : "bg-gray-50"
        )}>
          <div className="container mx-auto max-w-3xl">
            <div className={cn(
              "rounded-lg p-5 sm:p-8",
              darkMode ? "bg-gray-800" : "bg-white shadow-md"
            )}>
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-[#006B5F]" />
                <h2 className={cn(
                  "text-xl font-bold",
                  darkMode ? "text-white" : "text-gray-900"
                )}>
                  Dedication
                </h2>
              </div>
              <p className={cn(
                "leading-relaxed",
                darkMode ? "text-gray-300" : "text-gray-700"
              )}>
                {bookMetadata.dedication}
              </p>
            </div>
          </div>
        </section>

        {/* Main Content Area */}
        <section id="chapters-section" className="scroll-mt-20 px-4 py-10 md:px-6 md:py-12">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-[300px_1fr] gap-8">
              {/* Table of Contents - Floating button on mobile, sticky sidebar on desktop */}
              <BookTableOfContents chapters={chapters} darkMode={darkMode} />

              {/* Chapters */}
              <main
                className={cn(
                  "max-w-3xl mx-auto lg:mx-0",
                  darkMode ? "text-gray-100" : "text-gray-900"
                )}
                style={{ fontSize: `${fontSize}px` }}
              >
                {/* Greetings */}
                <div className={cn(
                  "mb-10 rounded-lg p-5 sm:mb-12 sm:p-8",
                  darkMode ? "bg-gray-800" : "bg-white shadow-md"
                )}>
                  <h2 className={cn(
                    "text-2xl font-bold mb-4",
                    darkMode ? "text-white" : "text-gray-900"
                  )}>
                    Greetings
                  </h2>
                  <div className={cn(
                    "leading-relaxed whitespace-pre-line",
                    darkMode ? "text-gray-300" : "text-gray-700"
                  )}>
                    {bookMetadata.greetings}
                  </div>
                </div>

                {/* Thanks */}
                <div className={cn(
                  "mb-10 rounded-lg p-5 sm:mb-12 sm:p-8",
                  darkMode ? "bg-gray-800" : "bg-white shadow-md"
                )}>
                  <h2 className={cn(
                    "text-2xl font-bold mb-4",
                    darkMode ? "text-white" : "text-gray-900"
                  )}>
                    Thanks
                  </h2>
                  <p className={cn(
                    "leading-relaxed",
                    darkMode ? "text-gray-300" : "text-gray-700"
                  )}>
                    {bookMetadata.thanks}
                  </p>
                </div>

                {/* All Chapters */}
                {chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className={cn(
                      "mb-8 rounded-lg p-5 sm:p-8",
                      darkMode ? "bg-gray-800" : "bg-white shadow-md"
                    )}
                  >
                    <BookChapter chapter={chapter} darkMode={darkMode} />
                  </div>
                ))}
              </main>
            </div>
          </div>
        </section>
      </div>

      {/* Reading Controls */}
      <BookReadingControls
        fontSize={fontSize}
        onFontSizeChange={setFontSize}
        darkMode={darkMode}
        onDarkModeChange={setDarkMode}
      />
    </Navigation>
  );
};

export default BookOfComfort;
